<template>
  <v-expansion-panels popout v-model="openPanel" accordion tile>
    <v-expansion-panel
      v-for="(receivableByAccount, i) in receivablesByBusinessEntity"
      :key="receivableByAccount.businessEntityName"
    >
      <v-expansion-panel-header @click="assignTableHeightWithDelay">
        {{ receivableByAccount.businessEntityName }}
        <v-spacer/>
        <template #actions>
          {{ formatMoney(receivableByAccount.amount) }}
          <v-icon class="ml-2"
            >mdi-chevron-down</v-icon
          >
        </template>
      </v-expansion-panel-header>

      <v-expansion-panel-content>
        <v-data-table
          :id="'receivable-expansion-panel-table-'.concat(i)"
          v-if="receivableByAccount.receivables.length > 0"
          :headers="headers"
          :items.sync="receivableByAccount.receivables"
          :hide-default-footer="receivableByAccount.receivables.length < 10"
          dense
          :height="tableHeight"
          @update:options="tableOptionsUpdated"
        >
          <template v-slot:item.amount="{ item }">{{
            `${formatMoney(item.amount)}`
          }}</template>
          <template v-slot:item.penaltyAmount="{ item }">{{
            `${formatMoney(item.penaltyAmount)}`
          }}</template>
          <template v-slot:item.rate="{ item }">
            <span>{{ formatMoney(item.computedAtRate) }}</span>
          </template>
          <template #item.payByTons="{item}">{{ getPayByTonsText(item) }}</template>
          <template #item.payOn="{item}">{{
            payOn(item.computedAtPayOn)
          }}</template>
          <template #item.payBy="{item}">{{
            payBy(item.computedAtPayBy)
          }}</template>
          <template v-slot:item.createdAt="{ item }">{{
            utcToLocalDate(item.ticketCreatedAt)
          }}</template>
          <template #item.activityWithSplit="{item}">
            <Icon
            icon="weight-alert"
            :tooltipText="$t('incursPenalty')"
            :iconColor="item.penaltyAmount === 0 ? 'grey' : undefined"
            :small="false"
            v-if="item.specialization === TemplateSpecialization.Penalty.value"/>
            <span>
              {{ item.activityName }}
            </span>
            <span v-if="item.computedAtSplit">
              ({{ formatPercentage(item.computedAtSplit) }})
            </span>
          </template>
          <template v-slot:item.actions="{ item }">
            <Icon
              iconColor="black"
              icon="mdi-information-outline"
              :tooltipText="$t('details')"
              :disabled="item.recoveryMethod === 1"
              @icon-clicked="viewReceivable(item)"
            >
            </Icon>
          </template>
        </v-data-table>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script>
import { formatMoney, formatPercentage, tonStringFromPounds } from '@/utils/NumericMutations.js'
import { utcToLocalDate } from '@/utils/DateFormatter.js'
import { ticketReceivableHeaders } from '@/headers/Receivable.js'
import { PayOn, PayBy, TemplateSpecialization } from '@/utils/Enumerations.js'
import { tableHeight } from '@/utils/componentHelpers.js'
import { mapGetters } from 'vuex'
export default {
  name: 'ReceivableExpansionPanels',

  props: {
    receivables: {
      type: Array,
      required: true
    },
    ticket: {
      type: Object,
      required: false
    }
  },

  components: {
    Icon: () => import('@/components/helper/Icon.vue')
  },

  data: () => ({
    TemplateSpecialization,
    openPanel: 0,
    PayBy,
    currentTableOptions: {},
    tableHeight: 'auto'
  }),

  created () {
    this.openPanel = this.receivablesByBusinessEntity.length === 1 ? 0 : null
    this.assignTableHeightWithDelay()
  },

  computed: {
    ...mapGetters('dialog', ['topDialogState']),

    headers () {
      return ticketReceivableHeaders()
    },

    formattedReceivables () {
      return this.receivables.map(r => ({
        ...r,
        payByTons: tonStringFromPounds(r.payByWeight, 3, r.computedAtPayBy === PayBy.MetricWeight.value)
      }))
    },

    receivablesByBusinessEntity () {
      const receivablesByBusinessEntity = []
      this.formattedReceivables.forEach(p => {
        const entityIndex = receivablesByBusinessEntity.findIndex(pbv => pbv.businessEntityName === p.businessEntityName)
        const mappedReceivable = {
          ...p,
          activityWithSplit: {
            name: p.activityName,
            split: p.computedAtSplit
          }
        }
        if (entityIndex === -1) {
          receivablesByBusinessEntity.push({
            businessEntityName: p.businessEntityName,
            amount: p.amount,
            receivables: [mappedReceivable]
          })
        } else {
          receivablesByBusinessEntity[entityIndex].receivables.push(mappedReceivable)
          receivablesByBusinessEntity[entityIndex].amount += p.amount
        }
      })

      return receivablesByBusinessEntity
    }
  },

  watch: {
    topDialogState: {
      handler () {
        this.assignTableHeightWithDelay(150)
      },
      deep: true
    },

    '$vuetify.breakpoint.width' () { this.assignTableHeight() },
    '$vuetify.breakpoint.height' () { this.assignTableHeight() }
  },

  methods: {
    formatMoney,
    formatPercentage,
    tonStringFromPounds,
    utcToLocalDate,
    payOn: (x) => PayOn.fromInt(x),
    payBy: (x) => PayBy.fromInt(x),

    getPayByTonsText (item) {
      if (item.computedAtPayBy === PayBy.Load.value || item.computedAtPayBy === PayBy.Volume.value) return this.$t('notApplicable')
      // TODO: Remove case for corrections when server bug is fixed to return corrected weights on financials REMINDER 07/23/2025 Do this in August
      if (this.ticket.correctionType !== null) return tonStringFromPounds(item.computedAtPayOn === PayOn.Gross.value ? this.ticket.grossWeight : this.ticket.netWeight, 3, item.computedAtPayBy === PayBy.MetricWeight.value)
      return item.payByTons
    },

    tableOptionsUpdated (tableOptions) {
      this.currentTableOptions = tableOptions
    },

    assignTableHeightWithDelay (delay = 50) {
      setTimeout(() => {
        this.assignTableHeight()
      }, delay)
    },

    assignTableHeight () {
      this.tableHeight = tableHeight({
        tableElementId: 'receivable-expansion-panel-table-'.concat(this.openPanel),
        offset: 600,
        dataLength: this.receivablesByBusinessEntity?.length || 0
      })
    }
  }
}
</script>
